import { createEnum } from '../helpers'

export const CREDIT_INDEX_SCORES_IMPORT_ERRORS = [
  'zodSchemaError',
  'missingErp',
  'missingEquifax',
  'invalidYear',
  'invalidValue',
] as const

export const CREDIT_INDEX_SCORES_IMPORT_ERROR_TYPES = createEnum(CREDIT_INDEX_SCORES_IMPORT_ERRORS)
export type CreditIndexScoresImportErrorType = (typeof CREDIT_INDEX_SCORES_IMPORT_ERRORS)[number]

/**
 * Score Trend windowing options beyond the default (the selected calendar year, Jan–Dec).
 * Currently only 'last12' (the trailing 12 months from today). Add future windows here so the
 * `CreditIndexScoreRange` type expands in one place rather than at each inline `z.enum`/literal.
 */
export const CREDIT_INDEX_SCORE_RANGES = ['last12'] as const
export type CreditIndexScoreRange = (typeof CREDIT_INDEX_SCORE_RANGES)[number]

export const CREDIT_SCORE_TIERS = [
  'veryGood',
  'good',
  'average',
  'poor',
  'veryPoor',
  'bankrupt',
] as const

export type CreditScoreTier = (typeof CREDIT_SCORE_TIERS)[number]

/**
 * The Credit Index Score scale. 70 is the bankruptcy score and the top of the scale — Equifax
 * publishes nothing above it — so a value outside `[MIN, MAX]` is bad data, not a worse score. The
 * score import rejects such cells (`isValidCreditIndexScore`); the same check runs at read time so
 * a value that got in some other way is flagged on the customer page rather than trusted.
 */
export const CREDIT_INDEX_SCORE_MIN = 0
export const CREDIT_INDEX_SCORE_MAX = 70

/** Inclusive score range for each score tier — single source of truth for the mapping. */
export const CREDIT_SCORE_TIER_RANGES: Record<CreditScoreTier, { min: number; max: number }> = {
  veryGood: { min: 0, max: 9 },
  good: { min: 10, max: 20 },
  average: { min: 21, max: 30 },
  poor: { min: 31, max: 40 },
  veryPoor: { min: 41, max: 69 },
  bankrupt: { min: CREDIT_INDEX_SCORE_MAX, max: CREDIT_INDEX_SCORE_MAX },
}

/** An integer on the scale. */
export function isValidCreditIndexScore(value: number | null | undefined): value is number {
  return (
    value != null &&
    Number.isInteger(value) &&
    value >= CREDIT_INDEX_SCORE_MIN &&
    value <= CREDIT_INDEX_SCORE_MAX
  )
}

/** Present and off the scale. Null is "no score", which is missing, not invalid. */
export function hasInvalidCreditIndexScore(value: number | null | undefined): boolean {
  return value != null && !isValidCreditIndexScore(value)
}

/**
 * Tiers considered "high risk" for credit review events: a score entering one of these bands
 * flags the account for review (see `fuze_credit.review_events`).
 */
export const HIGH_RISK_CREDIT_SCORE_TIERS: readonly CreditScoreTier[] = ['veryPoor', 'bankrupt']

/** Position of a tier in `CREDIT_SCORE_TIERS` — lets callers measure how many bands a score moved. */
export function getCreditScoreTierIndex(tier: CreditScoreTier): number {
  return CREDIT_SCORE_TIERS.indexOf(tier)
}

/** The tier a score falls in; null for no score AND for a score off the scale (see `hasInvalidCreditIndexScore`). */
export function getCreditScoreTier(value?: number): CreditScoreTier | null {
  if (value === undefined) return null
  return (
    CREDIT_SCORE_TIERS.find((tier) => {
      const { min, max } = CREDIT_SCORE_TIER_RANGES[tier]
      return value >= min && value <= max
    }) ?? null
  )
}
